// @givmo/org-console — MODULES: the organization's own apps, as the person who decides sees them.
//
// ══ THE MANAGEMENT PROJECTION, NOT THE APP GRID ════════════════════════════════════════════════
//
// The app grid answers "what can I open"; this answers "what does this organization have, and is it
// on". Same five-state verdict underneath (`./module-verdict.ts`, over a normalized
// `{known, held, orgEnabled}`) — different affordances and a different voice, because the reader here
// is the admin. Two consequences follow and both are the whole reason this is a separate surface:
//
//   * "Ask your admin" is never said here. It names a decision the reader is standing in front of.
//     A module they do not hold states that as a quiet secondary fact and nothing more.
//   * A switched-off module carries the actual SWITCH rather than a "Turn on" link into the page it
//     is already on.
//
// ⚠ WHICH MODULES EXIST COMES FROM THE SETTINGS READ, NOT FROM THE STANDING READ. `access/settings`
// carries every module the organization's class knows, with the org-wide value already resolved — so
// key-presence there IS "offered to this kind of organization", and a module the class does not carry
// is ABSENT by never appearing rather than by a rule. The caller's own standing supplies only whether
// THEY hold each one, and it fills in permissively: a standing that reported nothing about a module
// must not tell a Global Admin they lack it.
//
// ⚠ …NARROWED TO THE APPS PEOPLE CAN OPEN HERE (OM-95, D-CPO-77 item 5). The settings read is the
// class's apps, released and offered, and cannot know which of them this build mounts; the host's
// `servedModules` (the grid's own set) can. So an app nobody can open in this environment has no card
// and no switch, and the sentence about turning apps off shows only when a card here can be switched.
// Hiding a card writes nothing: the organization's value for it is left as it is.
//
// ⚠ THE WRITE IS NOT OPTIMISTIC, and that is the same discipline the switch has always had here: a
// control that flips instantly and silently reverts is indistinguishable from one that worked — on
// the one control that closes an app for everyone in the organization.
//
// ⚠ THIS PANEL RENDERS NO HEADING OF ITS OWN. It is a page's whole content, not one section among
// several, so the host's page header names it — which is also what keeps one noun off the screen
// twice. What it DOES own is the sentence explaining what the switch does, because that is copy about
// its own control rather than about the page.
//
// ══ SHELL CONTRACT v3 (OM-14) — what the page gained ══════════════════════════════════════════
//
//   * TERMS AT TURN ON (T9): a module whose settings row says `terms_state: 'required'` opens the
//     terms modal BEFORE the switch write (or the hook start); the acceptance lands, then the
//     original act runs. A failed hook returns the module to OFF and the acceptance stands.
//   * ALWAYS-ON (the third activation mode): a row with `switchable: false` renders NO switch — on,
//     un-toggleable, no Turn On ceremony — and reads "Always on".
//   * THE COUNT: "N people can open it" while the app opens, "N people have access" while it is
//     off or setting up (mockup requirement 6) — counted over the EXHAUSTED roster, and until the
//     roster is exhausted the door reads "Who can open it" with no number, because a count is a
//     claim (see `useOrgMembersExhausted`).
//   * THE SETTINGS ENTRY (⋯) that carried "Who can approve requests for this app" (R9) is GONE: the
//     setting was dropped on every app of both consoles (EP-WEB36, D-EP-58 — a module's Owners and
//     the Global Admins always approve), and a menu with nothing in it is a decorative control.
//   * THE PENDING STRIP: "N people are asking for access → Team access", when the count is non-zero,
//     off the caller's own standing read.
//   * THE CLASS DEFAULT LEADS (D-CP-54, 2026-09-04): the card of `config.classDefault` — the class's
//     default landing module, when the host names one — renders FIRST; every other card keeps the
//     settings read's order. No class default, or one the settings map does not carry, ⇒ the wire
//     order untouched (never a phantom card).
//
// DATA CONTRACT: the `<prefix>-modules-*` test-id family (+ `-count`, and `<prefix>-pending-strip`).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  ErrorMessage,
  FOCUS_RING,
  Switch,
  cn,
  shouldInterceptClick,
  testIdProp,
} from '@givmo/design-system';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import { isServedModule, testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { ModuleTermsDialog } from './org-module-terms-dialog';
import { TEAM_COPY } from './org-team-copy';
import { isOrgConsoleUnavailable } from './org-console-transport';
import {
  moduleVerdict,
  normalizeModuleActivationReason,
  normalizeModuleActivationState,
  standingsFromModuleRows,
  turnOnMechanism,
  PROVISIONING_HOOK_MODULES,
  type ModuleVerdict,
  type ModuleActivationReason,
  type ModuleActivationState,
} from './module-verdict';
import { moduleActivationInvalidations, orgConsoleKeys } from './org-access-queries';
import { CAPABILITY_WRITE_SETTINGS, holdsCapability } from './standing-read';
import {
  useOrgAccessGate,
  useOrgAccessSettings,
  useOrgMembersExhausted,
  useUpdateOrgAccessSettings,
} from './use-org-console';
import {
  OrgConsoleUnavailableNotice,
  OrgNotPermittedNotice,
  OrgTierUnknownNotice,
} from './org-console-notices';
import type {
  OrgAccessSettings,
  OrgModuleActivationResult,
  OrgModuleTermsState,
} from './wire-types';

/** The heading the notices carry, since the panel itself renders none. */
const NOTICE_TITLE = 'Apps';

/** Sequential retry cadence. It grows quickly, then stays bounded for provisioning-dark staging. */
export const PROVISIONING_RETRY_BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

export function provisioningRetryDelay(attempt: number): number {
  const index = Math.max(
    0,
    Math.min(Math.floor(attempt), PROVISIONING_RETRY_BACKOFF_MS.length - 1),
  );
  return PROVISIONING_RETRY_BACKOFF_MS[index]!;
}

function waitForProvisioningRetry(delayMs: number, signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (shouldContinue: boolean) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener('abort', onAbort);
      resolve(shouldContinue);
    };
    const timer = window.setTimeout(() => finish(true), delayMs);
    const onAbort = () => {
      window.clearTimeout(timer);
      finish(false);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Full curated reason text: the typed headline plus every safe organization action. */
export function activationReasonText(reason: ModuleActivationReason): string {
  const actions = reason.actions.map((action) => action.message);
  return actions.length === 0 ? reason.message : `${reason.message} ${actions.join(' · ')}`;
}

interface ProvisioningRun {
  module: string;
  controller: AbortController;
  scope: ActivationScope;
  generation: number;
}

interface ActivationScope {
  orgRef: string;
  handler: OrgModulesPanelProps['onStartProvisioning'];
  hookSetKey: string;
}

interface ScopedModuleState {
  scope: ActivationScope;
  module: string;
}

interface ModuleFailure extends ScopedModuleState {
  operation: 'activation' | 'settings';
}

function cacheActivationResult(
  queryClient: QueryClient,
  orgRef: string,
  module: string,
  activationState: ModuleActivationState,
  activationReason: ModuleActivationReason | null,
): void {
  queryClient.setQueryData<OrgAccessSettings | null>(orgConsoleKeys(orgRef).settings, (current) => {
    if (current === undefined || current === null) return current;
    return {
      ...current,
      modules: { ...current.modules, [module]: activationState === 'on' },
      activations: {
        ...current.activations,
        [module]: {
          activation_state: activationState,
          activation_reason: activationState === 'off' ? activationReason : null,
        },
      },
    };
  });
}

export interface OrgModulesPanelProps {
  config: OrgConsoleConfig;
  /**
   * Modules whose switch starts provisioning work rather than writing the org-wide setting.
   *
   * Defaults to the shared authority (`books` is its first member). Tests and exact future extensions
   * may inject a set, but production hosts normally leave this alone.
   */
  provisioningHooks?: readonly string[];
  /**
   * Start a hook module's provisioning.
   *
   * ⚠ A HOOK MODULE WITH NO HANDLER RENDERS NO SWITCH, which is the same law every other state here
   * obeys: the control that cannot perform its act is not drawn. The org-wide write is NOT a
   * fallback for it — a hook module's "on" is the completed setup, so writing the setting would
   * report a module as on while the thing behind it does not exist.
   */
  onStartProvisioning?: (module: string) => Promise<OrgModuleActivationResult>;
}

/** One module's row, already reduced to what the tile renders. */
interface ModuleRow {
  module: string;
  label: string;
  enabled: boolean;
  activationState: ModuleActivationState | null;
  activationReason: ModuleActivationReason | null;
  hooked: boolean;
  /**
   * The shared verdict. `absent` is unreachable here — the row exists because the class carries the
   * module — so only `locked` changes what this tile renders.
   */
  verdict: ModuleVerdict;
  /** The switch is drawn only when pressing it would do something. */
  canSwitch: boolean;
  /** `false` on an always-on module: no switch, no ceremony, "Always on". */
  switchable: boolean;
  /** Whether Turn On must present an instrument first (`required`), or has (`accepted`), or never (`none`). */
  termsState: OrgModuleTermsState;
  /** Where "who can open it" goes, when this host routed it. */
  accessHref: string | undefined;
}

/**
 * The switch write's terms refusal (T9.3 — PR-BE3 `5fec8d4e1`: 409 `MODULE_TERMS_REQUIRED`, with
 * `context.module` + `context.terms_path`). Detected on the CODE, never on the server's English.
 */
function isModuleTermsRequired(error: unknown): boolean {
  return (
    isGivmoError(error) && error.status === 409 && firstErrorCode(error) === 'MODULE_TERMS_REQUIRED'
  );
}

/** The count line, honest about whether the app currently OPENS (mockup requirement 6). */
export function holderCountLine(count: number, opens: boolean): string {
  const people = count === 1 ? '1 person' : `${count} people`;
  return opens ? `${people} can open it` : `${people} ${count === 1 ? 'has' : 'have'} access`;
}

/**
 * The class default's row FIRST, every other row in its existing order (D-CP-54) — a stable
 * partition. No class default, one the rows do not carry, or one already leading ⇒ the rows exactly
 * as they came, so a host that passes nothing renders byte for byte what it rendered before.
 */
function leadWithClassDefault(rows: ModuleRow[], classDefault: string | undefined): ModuleRow[] {
  if (classDefault === undefined) return rows;
  const index = rows.findIndex((row) => row.module === classDefault);
  if (index <= 0) return rows;
  return [rows[index]!, ...rows.slice(0, index), ...rows.slice(index + 1)];
}

export function OrgModulesPanel({
  config,
  provisioningHooks = PROVISIONING_HOOK_MODULES,
  onStartProvisioning,
}: OrgModulesPanelProps) {
  const prefix = `${testIdPrefix(config)}-modules`;
  const gate = useOrgAccessGate(config.orgRef);
  const settings = useOrgAccessSettings(config.orgRef, gate.isGlobalAdminConfirmed);
  const update = useUpdateOrgAccessSettings(config.orgRef, config.onAccessWritten);
  const queryClient = useQueryClient();
  const normalizedProvisioningHooks = useMemo(
    () => [...new Set(provisioningHooks)].sort(),
    [provisioningHooks],
  );
  const hookSetKey = normalizedProvisioningHooks.join('\u001f');
  const activationScope = useMemo<ActivationScope>(
    () => ({ orgRef: config.orgRef, handler: onStartProvisioning, hookSetKey }),
    [config.orgRef, onStartProvisioning, hookSetKey],
  );
  const [failedState, setFailedState] = useState<ModuleFailure | null>(null);
  const [provisioningState, setProvisioningState] = useState<ScopedModuleState | null>(null);
  const activeRunRef = useRef<ProvisioningRun | null>(null);
  const generationRef = useRef(0);
  // Shell contract v3: the terms modal a Turn On may open first.
  const [termsState, setTermsState] = useState<ScopedModuleState | null>(null);
  const termsRunRef = useRef<ScopedModuleState | null>(null);
  const termsFor = termsState?.scope === activationScope ? termsState.module : null;
  const offerTerms = (module: string, scope: ActivationScope) => {
    const continuation = { module, scope };
    termsRunRef.current = continuation;
    setTermsState(continuation);
  };
  const closeTerms = () => {
    termsRunRef.current = null;
    setTermsState(null);
  };
  // The module whose terms modal has ALREADY been shown for the act in flight — the one-shot guard
  // behind the switch write's 409 catch (see `turnOn`). Reset by every fresh flip.
  const termsRetryRef = useRef<string | null>(null);
  // The exhausted roster, for the count line — read only when there IS a door to count for (the
  // host routed `links.moduleAccess`) and for a holder of the Apps page's own capability. A host
  // with no door renders no count, and this page then makes exactly the reads it always made.
  //
  // ⚠ GATED ON THE CAPABILITY, never the admin flag (the build law, T10.5): the count is the Apps
  // page's claim, and the Apps page is a `write_settings` holder's (T4.4).
  const roster = useOrgMembersExhausted(
    config.orgRef,
    holdsCapability(gate.self?.capabilities ?? null, CAPABILITY_WRITE_SETTINGS) &&
      config.links?.moduleAccess !== undefined,
  );

  const failedModule = failedState?.scope === activationScope ? failedState.module : null;
  const provisioningModule =
    provisioningState?.scope === activationScope ? provisioningState.module : null;

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    activeRunRef.current?.controller.abort();
    activeRunRef.current = null;
    setProvisioningState(null);
    setFailedState(null);
    termsRunRef.current = null;
    setTermsState(null);
    termsRetryRef.current = null;

    return () => {
      if (generationRef.current === generation) generationRef.current += 1;
      const active = activeRunRef.current;
      if (active?.scope === activationScope) {
        active.controller.abort();
        activeRunRef.current = null;
      }
    };
  }, [activationScope]);

  const standings = useMemo(() => standingsFromModuleRows(gate.self?.modules), [gate.self]);

  // The offered set, in the settings read's order.
  const offered: ModuleRow[] = useMemo(
    () =>
      // Only the apps people can open here (OM-95) — the grid's set, so no switch for an app nobody
      // can open in this environment.
      Object.entries(settings.data?.modules ?? {})
        .filter(([module]) => isServedModule(config, module))
        .map(([module, enabled]) => {
          const hooked =
            turnOnMechanism(module, normalizedProvisioningHooks) === 'provisioning-hook';
          const activation = settings.data?.activations[module];
          const activationState = activation?.activation_state ?? null;
          const switchable = settings.data?.switchable?.[module] !== false;
          return {
            module,
            label: config.vocab.moduleLabel(module),
            enabled,
            activationState,
            activationReason:
              hooked && onStartProvisioning !== undefined && activationState === 'off'
                ? (activation?.activation_reason ?? null)
                : null,
            hooked,
            // `known` is true by construction: this row came from the map of what the class carries.
            // `held` fills in permissively — see the header.
            verdict: moduleVerdict({
              known: true,
              held: standings?.[module]?.held ?? true,
              orgEnabled: enabled,
              ...(activationState === null ? {} : { activationState }),
            }),
            // An always-on module has no switch at all — the third activation mode.
            canSwitch: switchable && (!hooked || onStartProvisioning !== undefined),
            switchable,
            termsState: settings.data?.terms?.[module] ?? 'none',
            accessHref: config.links?.moduleAccess?.(module),
          };
        }),
    [settings.data, standings, normalizedProvisioningHooks, onStartProvisioning, config],
  );
  // …then the class default's card moved to the front (D-CP-54) — a memo beside the offered set,
  // so nothing else re-renders differently.
  const rows = useMemo(
    () => leadWithClassDefault(offered, config.classDefault),
    [offered, config.classDefault],
  );

  if (gate.isUnavailable || (settings.isError && isOrgConsoleUnavailable(settings.error))) {
    return (
      <OrgConsoleUnavailableNotice
        testId={`${prefix}-unavailable`}
        title={NOTICE_TITLE}
        description="This environment doesn’t have the console turned on, so there are no apps to show yet."
      />
    );
  }

  if (gate.isPending) {
    return (
      <Card>
        <CardBody>
          <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-loading`)}>
            Loading…
          </p>
        </CardBody>
      </Card>
    );
  }

  if (gate.tierUnknown) {
    return <OrgTierUnknownNotice testId={`${prefix}-tier-retry`} onRetry={gate.refetch} />;
  }

  if (!gate.isGlobalAdminConfirmed) {
    return (
      <OrgNotPermittedNotice
        testId={`${prefix}-not-permitted`}
        title="Only a Global Admin can change this"
        description={`Ask a Global Admin at your ${config.vocab.orgNoun} to turn an app on or off.`}
      />
    );
  }

  /**
   * Flip one module.
   *
   * The hook branch is the seam D-CPO-26 R-1 reserves: a module that declares one is turned ON by its
   * own activation call, never by this write, and the org-wide setting is what turns it OFF again.
   * Books is the first hook module. Its callback is sequential and self-healing: only an exact
   * `setting-up` answer schedules another call, after bounded backoff. ON/OFF are terminal.
   */
  const startProvisioning = async (
    module: string,
    activate: NonNullable<OrgModulesPanelProps['onStartProvisioning']>,
  ): Promise<void> => {
    const existing = activeRunRef.current;
    existing?.controller.abort();
    const run: ProvisioningRun = {
      module,
      controller: new AbortController(),
      scope: activationScope,
      generation: generationRef.current,
    };
    activeRunRef.current = run;
    setProvisioningState({ scope: activationScope, module });

    const isCurrent = (): boolean =>
      !run.controller.signal.aborted &&
      activeRunRef.current === run &&
      generationRef.current === run.generation &&
      run.scope === activationScope;

    try {
      let retryAttempt = 0;
      for (;;) {
        const result = await activate(module);
        if (!isCurrent()) return;

        const activationState = normalizeModuleActivationState(result.activation_state);
        if (
          activationState === null ||
          result.org_ref !== run.scope.orgRef ||
          result.module !== module ||
          result.enabled !== (activationState === 'on')
        ) {
          throw new Error('The activation operation returned a mismatched result');
        }
        const activationReason =
          activationState === 'off'
            ? normalizeModuleActivationReason(result.activation_reason)
            : null;
        if (!isCurrent()) return;
        cacheActivationResult(
          queryClient,
          run.scope.orgRef,
          module,
          activationState,
          activationReason,
        );
        if (!isCurrent()) return;
        for (const queryKey of moduleActivationInvalidations(run.scope.orgRef)) {
          void queryClient.invalidateQueries({ queryKey, exact: true });
        }
        if (!isCurrent()) return;
        config.onAccessWritten?.();

        if (activationState !== 'setting-up') return;
        const shouldContinue = await waitForProvisioningRetry(
          provisioningRetryDelay(retryAttempt),
          run.controller.signal,
        );
        if (!shouldContinue || !isCurrent()) return;
        retryAttempt += 1;
      }
    } catch (error) {
      if (isCurrent()) {
        if (isModuleTermsRequired(error) && termsRetryRef.current !== module) {
          termsRetryRef.current = module;
          offerTerms(module, run.scope);
        } else {
          setFailedState({ scope: activationScope, module, operation: 'activation' });
        }
      }
    } finally {
      if (activeRunRef.current === run) {
        const wasCurrent = isCurrent();
        activeRunRef.current = null;
        if (wasCurrent) setProvisioningState(null);
      }
    }
  };

  /**
   * The act a Turn On performs once any terms are accepted: the hook start, or the settings write.
   *
   * ⚠ THE SERVER IS THE ONE INVARIANT (T9.3), AND ITS REFUSAL IS THE PAIRING RULE'S CATCH. A
   * settings read that predates `terms_state`, or a write that races a newly registered
   * instrument, reaches the PUT without the modal — and the server answers 409
   * `MODULE_TERMS_REQUIRED`. That opens the modal and re-runs this act once the acceptance has
   * landed — ONCE: a second refusal after the modal has answered (a pair that is bound but has no
   * effective version) renders as the ordinary failure, never as a loop.
   */
  const turnOn = (row: ModuleRow): void => {
    if (row.hooked) {
      // Belt behind the hidden switch: a JS caller or stale event can never fall through to PUT.
      if (onStartProvisioning === undefined) return;
      void startProvisioning(row.module, onStartProvisioning);
      return;
    }
    const writeScope = activationScope;
    update.mutate(
      { modules: { [row.module]: { enabled: true } } },
      {
        onSuccess: () => {
          if (termsRetryRef.current === row.module) termsRetryRef.current = null;
        },
        onError: (error) => {
          if (isModuleTermsRequired(error) && termsRetryRef.current !== row.module) {
            termsRetryRef.current = row.module;
            offerTerms(row.module, writeScope);
            return;
          }
          setFailedState({ scope: writeScope, module: row.module, operation: 'settings' });
        },
      },
    );
  };

  const flip = (row: ModuleRow, checked: boolean): void => {
    setFailedState(null);
    termsRetryRef.current = null;
    if (!row.switchable) return;
    if (checked) {
      // ⚠ TERMS FIRST (T9.3): a module whose class binds an instrument the organization has not
      // accepted opens the modal; the switch write (or the hook start) runs only once the
      // acceptance has landed. The server refuses the write either way — this is the client's
      // half of ONE server invariant, not a second gate.
      if (row.termsState === 'required') {
        termsRetryRef.current = row.module;
        offerTerms(row.module, activationScope);
        return;
      }
      turnOn(row);
      return;
    }
    const writeScope = activationScope;
    update.mutate(
      { modules: { [row.module]: { enabled: false } } },
      {
        onError: () =>
          setFailedState({ scope: writeScope, module: row.module, operation: 'settings' }),
      },
    );
  };

  const pendingRequests = gate.self?.pending_access_requests ?? 0;
  const teamAccessHref = config.links?.teamAccess;
  const termsRow = termsFor === null ? null : (rows.find((row) => row.module === termsFor) ?? null);
  const holders = roster.isSuccess ? roster.data : null;

  return (
    <section className="flex flex-col gap-16" {...testIdProp(prefix)}>
      {/* The pending-requests strip (R4's "table on the admin console dashboard" half, OQ4). */}
      {pendingRequests > 0 && (
        <Card testId={`${prefix}-pending-strip`}>
          <CardBody className="flex flex-wrap items-center justify-between gap-12">
            <p
              className="font-sans text-s14 font-w500 tracking-givmo text-text-strong"
              {...testIdProp(`${prefix}-pending-strip-count`)}
            >
              {pendingRequests} {pendingRequests === 1 ? 'person is' : 'people are'} asking for
              access
            </p>
            {teamAccessHref !== undefined && (
              <a
                href={teamAccessHref}
                onClick={(event) => {
                  if (config.onNavigate === undefined || !shouldInterceptClick(event)) return;
                  event.preventDefault();
                  config.onNavigate(teamAccessHref);
                }}
                className={cn(
                  'rounded-sm font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700',
                  FOCUS_RING,
                )}
                {...testIdProp(`${prefix}-pending-strip-link`)}
              >
                {TEAM_COPY.adminTeam} →
              </a>
            )}
          </CardBody>
        </Card>
      )}
      {/* ⚠ IT NAMES THE ORGANIZATION, and that is load-bearing rather than warmth. "everyone here"
          is answered by the chrome on a desktop breadcrumb and by nothing at all on a phone, on the
          console's highest-consequence surface. It shows only when a card here has a switch
          (OM-95's round 4): with Donations alone, always on, it described a switch that isn't there. */}
      {rows.some((row) => row.canSwitch) && (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          {...testIdProp(`${prefix}-switch-note`)}
        >
          Turning an app off closes it for everyone at{' '}
          {config.vocab.orgName !== undefined && config.vocab.orgName.trim().length > 0
            ? config.vocab.orgName.trim()
            : `your ${config.vocab.orgNoun}`}{' '}
          — including Global Admins. Nobody loses their access; the app just stops opening until you
          turn it back on.
        </p>
      )}

      {settings.isPending && (
        <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-loading`)}>
          Loading your apps…
        </p>
      )}

      {settings.isError && !isOrgConsoleUnavailable(settings.error) && (
        <ErrorMessage testId={`${prefix}-error`}>We couldn’t load your apps.</ErrorMessage>
      )}

      {rows.length > 0 && (
        <ul className="grid gap-12 sm:grid-cols-2" {...testIdProp(`${prefix}-list`)}>
          {rows.map((row) => (
            <li key={row.module}>
              <Card className="h-full" testId={`${prefix}-module-${row.module}`}>
                <CardBody className="flex h-full flex-col gap-12">
                  {/* A switch sits at the row's top; a word ("Always on", "On", "Off") sits on the
                      title's baseline. */}
                  <div
                    className={cn(
                      'flex justify-between gap-16',
                      row.canSwitch ? 'items-start' : 'items-baseline',
                    )}
                  >
                    <h3 className="min-w-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {row.label}
                    </h3>
                    {row.canSwitch ? (
                      <div className="flex shrink-0 items-center gap-8">
                        {row.activationState === 'setting-up' && (
                          <span
                            className="font-sans text-s12 font-w500 tracking-givmo text-text-tertiary"
                            {...testIdProp(`${prefix}-module-${row.module}-state`)}
                          >
                            Setting up
                          </span>
                        )}
                        <Switch
                          appearance="saas"
                          checked={row.enabled}
                          disabled={update.isPending || provisioningModule !== null}
                          onCheckedChange={(checked) => flip(row, checked)}
                          aria-label={`${row.label} enabled`}
                          testId={`${prefix}-module-${row.module}-switch`}
                        />
                      </div>
                    ) : (
                      <span
                        className="shrink-0 font-sans text-s12 font-w500 tracking-givmo text-text-tertiary"
                        {...testIdProp(`${prefix}-module-${row.module}-state`)}
                      >
                        {!row.switchable
                          ? 'Always on'
                          : row.activationState === 'setting-up'
                            ? 'Setting up'
                            : row.enabled
                              ? 'On'
                              : 'Off'}
                      </span>
                    )}
                  </div>

                  {/* The management voice: a module this person does not hold is a fact about them,
                      never a plea to an admin they are. */}
                  {row.verdict === 'locked' && (
                    <p
                      className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                      {...testIdProp(`${prefix}-module-${row.module}-locked`)}
                    >
                      You don’t have access to this one.
                    </p>
                  )}

                  {failedModule === row.module ? (
                    <ErrorMessage testId={`${prefix}-module-${row.module}-error`}>
                      {failedState?.operation === 'activation'
                        ? 'We couldn’t confirm setup finished. Please try again.'
                        : 'We couldn’t change this. Nothing was saved — please try again.'}
                    </ErrorMessage>
                  ) : row.activationReason !== null ? (
                    <ErrorMessage testId={`${prefix}-module-${row.module}-error`}>
                      {activationReasonText(row.activationReason)}
                    </ErrorMessage>
                  ) : null}

                  {/* Grants outlive the switch, so this stays true — and useful — while a module is
                      off. Absent when the host routed nowhere: no link beats a link to nothing.
                      The COUNT appears only once the roster is exhausted (a count is a claim);
                      "can open it" only while the app actually opens, "have access" otherwise. */}
                  {row.accessHref !== undefined && (
                    <a
                      href={row.accessHref}
                      className="mt-auto font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                      onClick={(event) => {
                        if (
                          config.onOpenModuleAccess === undefined ||
                          event.defaultPrevented ||
                          event.button !== 0 ||
                          event.metaKey ||
                          event.ctrlKey ||
                          event.shiftKey ||
                          event.altKey
                        ) {
                          return;
                        }
                        event.preventDefault();
                        config.onOpenModuleAccess(row.module);
                      }}
                      {...testIdProp(`${prefix}-module-${row.module}-access`)}
                    >
                      {holders === null ? (
                        'Who can open it'
                      ) : (
                        <span {...testIdProp(`${prefix}-module-${row.module}-count`)}>
                          {holderCountLine(
                            holders.filter((member) =>
                              member.modules.some(
                                (entry) => entry.module === row.module && entry.has_access,
                              ),
                            ).length,
                            row.enabled && row.activationState !== 'setting-up',
                          )}
                        </span>
                      )}
                    </a>
                  )}
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <ModuleTermsDialog
        key={config.orgRef}
        config={config}
        open={termsRow !== null}
        onOpenChange={(open) => !open && closeTerms()}
        module={termsRow?.module ?? null}
        onAccepted={() => {
          // The acceptance landed (or there was nothing to accept): the original act runs now.
          if (termsRow !== null && termsState !== null && termsRunRef.current === termsState) {
            turnOn(termsRow);
          }
          closeTerms();
        }}
      />
    </section>
  );
}
